import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, CheckCheck } from 'lucide-react';
import { apiUrl } from '../config/api';
import { useDetailsModal } from '../context/DetailsModalContext';
import { formatDateTimeDMY } from '../utils/dateUtils';

// Topbar notification bell: unread badge + dropdown listing the client's
// notifications (GET /api/notifications). Clicking an item marks it read
// and opens the loan it is about (or, with no loan, expands the full message).
// Staff accounts simply see an empty list.
const NotificationsBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const panelRef = useRef(null);
  const { openLoan } = useDetailsModal();

  const fetchNotifications = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(apiUrl('/api/notifications'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setNotifications(Array.isArray(data) ? data : []);
    } catch {
      /* keep whatever we had */
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close the panel when clicking outside it.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const unread = notifications.filter((n) => !n.is_read).length;

  const markRead = async (ids) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      await fetch(apiUrl('/api/notifications/read'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(ids ? { ids } : {}),
      });
    } catch {
      /* optimistic UI below still applies */
    }
  };

  const handleItemClick = (n) => {
    if (n.loan_id) {
      setOpen(false);
      openLoan(n.loan_id);
    } else {
      setExpandedId(expandedId === n.id ? null : n.id);
    }
    if (!n.is_read) {
      setNotifications((prev) =>
        prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x))
      );
      markRead([n.id]);
    }
  };

  const handleMarkAll = () => {
    setNotifications((prev) => prev.map((x) => ({ ...x, is_read: true })));
    markRead(null);
  };

  const formatWhen = (value) => {
    const s = formatDateTimeDMY(value);
    return s === '-' ? '' : s;
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Notifications"
        aria-label="Notifications"
        className="relative p-2 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/60 hover:text-blue-600 dark:hover:text-blue-300 transition-colors"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 max-h-[70vh] overflow-hidden flex flex-col rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl z-50">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 dark:border-slate-700">
            <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
              Notifications{unread > 0 ? ` (${unread} unread)` : ''}
            </span>
            {unread > 0 && (
              <button
                type="button"
                onClick={handleMarkAll}
                className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
              >
                <CheckCheck size={14} /> Mark all read
              </button>
            )}
          </div>
          <div className="overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                No notifications yet.
              </div>
            ) : (
              notifications.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => handleItemClick(n)}
                  className={`w-full text-left px-4 py-3 border-b border-slate-100 dark:border-slate-700/60 hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors ${
                    n.is_read ? '' : 'bg-blue-50/60 dark:bg-blue-900/20'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {!n.is_read && (
                      <span className="mt-1.5 w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p
                        className={`text-sm truncate ${
                          n.is_read
                            ? 'text-slate-700 dark:text-slate-200'
                            : 'font-semibold text-slate-900 dark:text-slate-100'
                        }`}
                      >
                        {n.title}
                      </p>
                      <p
                        className={`text-xs text-slate-500 dark:text-slate-400 mt-0.5 whitespace-pre-wrap ${
                          expandedId === n.id ? '' : 'line-clamp-2'
                        }`}
                      >
                        {n.body}
                      </p>
                      <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">
                        {formatWhen(n.created_at)}
                        {n.loan_id && (
                          <span className="ml-2 text-blue-600 dark:text-blue-400 hover:underline">View loan →</span>
                        )}
                      </p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsBell;
